import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import { HeaderStickyBanner } from "@/components/AdPlacements";
import {
  Tractor, Horse, Wrench, Leaf, MapPin, Tag, 
  MagnifyingGlass, Plus, Heart, Eye, CaretDown,
  FunnelSimple, X, CircleNotch, ChatsCircle, Storefront, HandHeart
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_ICONS = {
  machinery: Tractor,
  livestock: Horse,
  equipment: Wrench,
  feed: Leaf,
  land: MapPin,
  services: Tag,
  other: Tag
};

const CATEGORY_COLORS = {
  machinery: "bg-blue-600",
  livestock: "bg-green-600",
  equipment: "bg-orange-600",
  feed: "bg-lime-600",
  land: "bg-amber-700",
  services: "bg-purple-600",
  other: "bg-gray-600"
};

export default function MarketplacePage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [counties, setCounties] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  
  // Filters
  const [listingType, setListingType] = useState(searchParams.get("type") || "for_sale");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [subcategory, setSubcategory] = useState(searchParams.get("subcategory") || "");
  const [county, setCounty] = useState(searchParams.get("county") || "");
  const [minPrice, setMinPrice] = useState(searchParams.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("max_price") || "");
  const [condition, setCondition] = useState(searchParams.get("condition") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [showFilters, setShowFilters] = useState(false);
  
  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/categories`);
      setCategories(res.data.categories);
      setSubcategories(res.data.subcategories);
      setCounties(res.data.counties);
      setConditions(res.data.conditions);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  }, []);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort, listing_type: listingType };
      if (category) params.category = category;
      if (subcategory) params.subcategory = subcategory;
      if (county) params.county = county;
      if (minPrice) params.min_price = minPrice;
      if (maxPrice) params.max_price = maxPrice;
      if (condition) params.condition = condition;
      if (search) params.search = search;
      
      const res = await axios.get(`${API}/api/marketplace/listings`, { params });
      setListings(res.data.listings);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch listings:", err);
    } finally {
      setLoading(false);
    }
  }, [page, sort, listingType, category, subcategory, county, minPrice, maxPrice, condition, search]);
  
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  useEffect(() => {
    fetchListings();
  }, [fetchListings]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchListings();
  };
  
  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setSubcategory("");
    setCounty("");
    setMinPrice("");
    setMaxPrice("");
    setCondition("");
    setSort("newest");
    setPage(1);
  };
  
  const formatPrice = (price, currency = "EUR") => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency
    }).format(price);
  };
  
  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEOHead {...PAGE_SEO.marketplace} url="/marketplace" />
      <HeaderStickyBanner />
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E]">
              Farm Marketplace
            </h1>
            <p className="text-[#6B6661] mt-2">
              Buy & sell machinery, livestock, equipment, and more
            </p>
          </div>
          {user && (
            <div className="flex items-center gap-3">
              <Link
                to="/marketplace/messages"
                className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD] transition-colors"
                data-testid="messages-btn"
              >
                <ChatsCircle size={20} />
                Messages
              </Link>
              <Link
                to="/marketplace/create"
                className="flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors"
                data-testid="create-listing-btn"
              >
                <Plus size={20} weight="bold" />
                Post Listing
              </Link>
            </div>
          )}
        </div>

        {/* For Sale / Wanted Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => { setListingType("for_sale"); setPage(1); }}
            data-testid="tab-for-sale"
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
              listingType === "for_sale"
                ? "bg-[#0F766E] text-white"
                : "bg-white border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
            }`}
          >
            <Storefront size={20} />
            For Sale
          </button>
          <button
            onClick={() => { setListingType("wanted"); setPage(1); }}
            data-testid="tab-wanted"
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
              listingType === "wanted"
                ? "bg-[#D97706] text-white"
                : "bg-white border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
            }`}
          >
            <HandHeart size={20} />
            Wanted
          </button>
        </div>
        
        {/* Category Quick Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => { setCategory(""); setSubcategory(""); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              !category ? "bg-[#0F766E] text-white" : "bg-white border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
            }`}
          >
            All
          </button>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || Tag;
            return (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setSubcategory(""); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  category === cat 
                    ? `${CATEGORY_COLORS[cat]} text-white` 
                    : "bg-white border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
                }`}
              >
                <Icon size={16} />
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            );
          })}
        </div>
        
        {/* Search and Filters */}
        <div className="bg-white border border-[#E0D4C8] p-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
              <input
                type="text"
                placeholder="Search listings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                data-testid="search-input"
              />
            </div>
            
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
            >
              <FunnelSimple size={18} />
              Filters
              <CaretDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
            
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="px-4 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="popular">Most Viewed</option>
            </select>
            
            <button
              type="submit"
              className="px-6 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors"
            >
              Search
            </button>
          </form>
          
          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#E0D4C8] grid grid-cols-2 md:grid-cols-4 gap-4">
              {category && subcategories[category] && (
                <select
                  value={subcategory}
                  onChange={(e) => { setSubcategory(e.target.value); setPage(1); }}
                  className="px-3 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                >
                  <option value="">All {category}</option>
                  {subcategories[category].map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              )}
              
              <select
                value={county}
                onChange={(e) => { setCounty(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
              >
                <option value="">All Counties</option>
                {counties.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              
              <select
                value={condition}
                onChange={(e) => { setCondition(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
              >
                <option value="">Any Condition</option>
                {conditions.map((c) => (
                  <option key={c} value={c}>{c.replace("_", " ").charAt(0).toUpperCase() + c.replace("_", " ").slice(1)}</option>
                ))}
              </select>
              
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min €"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                />
                <span className="text-[#6B6661]">-</span>
                <input
                  type="number"
                  placeholder="Max €"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                />
              </div>
              
              <button
                onClick={clearFilters}
                className="flex items-center justify-center gap-2 px-3 py-2 text-[#DC2626] hover:bg-red-50"
              >
                <X size={16} />
                Clear Filters
              </button>
            </div>
          )}
        </div>
        
        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-[#6B6661]">
            {total} {total === 1 ? "listing" : "listings"} found
          </p>
        </div>
        
        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch size={40} className="animate-spin text-[#0F766E]" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E0D4C8]">
            <Tractor size={64} className="mx-auto text-[#8B8580] mb-4" />
            <h3 className="text-xl text-[#6B6661] mb-2">No listings found</h3>
            <p className="text-[#6B6661] mb-6">Try adjusting your filters or search terms</p>
            {user && (
              <Link
                to="/marketplace/create"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F766E] text-white hover:bg-[#0D6B64]"
              >
                <Plus size={20} />
                Post the First Listing
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((listing, index) => {
              const Icon = CATEGORY_ICONS[listing.category] || Tag;
              
              // Insert native ad every 6th position (after index 5, 11, 17, etc.)
              const showAdAfter = (index + 1) % 6 === 0 && index < listings.length - 1;
              
              return (
                <React.Fragment key={listing.id}>
                  <Link
                    to={`/marketplace/${listing.id}`}
                    className="bg-white border border-[#E0D4C8] hover:shadow-lg transition-shadow group"
                    data-testid={`listing-${listing.id}`}
                  >
                    {/* Image */}
                    <div className="aspect-[4/3] bg-[#F6E9DD] relative overflow-hidden">
                      {listing.images && listing.images.length > 0 ? (
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon size={48} className="text-[#8B8580]" />
                        </div>
                      )}
                      <span className={`absolute top-2 left-2 px-2 py-1 text-xs text-white ${CATEGORY_COLORS[listing.category]}`}>
                        {listing.category}
                      </span>
                      {listing.negotiable && (
                        <span className="absolute top-2 right-2 px-2 py-1 text-xs bg-[#F59E0B] text-white">
                          Negotiable
                        </span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-medium text-[#33302E] line-clamp-2 mb-2 group-hover:text-[#0F766E]">
                        {listing.title}
                      </h3>
                      <p className="font-mono text-xl text-[#0F766E] mb-2">
                        {formatPrice(listing.price, listing.currency)}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-[#6B6661]">
                        <MapPin size={14} />
                        <span>{listing.county}</span>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E0D4C8] text-xs text-[#8B8580]">
                        <span className="flex items-center gap-1">
                          <Eye size={12} />
                          {listing.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart size={12} />
                          {listing.favorites}
                        </span>
                        <span>
                          {new Date(listing.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                  </Link>
                  
                  {/* Native Ad after every 6th listing */}
                </React.Fragment>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-[#E0D4C8] text-[#6B6661] disabled:opacity-50 hover:bg-[#F6E9DD]"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-[#6B6661]">
              Page {page} of {pages}
            </span>
            <button
              onClick={() => setPage(Math.min(pages, page + 1))}
              disabled={page === pages}
              className="px-4 py-2 border border-[#E0D4C8] text-[#6B6661] disabled:opacity-50 hover:bg-[#F6E9DD]"
            >
              Next
            </button>
          </div>
        )}

        {/* Bottom Leaderboard Ad */}
        <div className="mt-8">
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
